import React, { useState, useEffect } from "react";
import AceEditor from "react-ace";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Textarea } from "../ui/textarea";
import "brace/mode/json";
import "brace/theme/github";
import { useToast } from "../ui/use-toast";

interface JsonInputFieldProps {
    apis: any[];
    setApis: (apis: any[]) => void;
    preview?: boolean;
    api: {
        input: {
            body: object | string;
            description: string;
        };
    };
    index: number;
}

const JsonInputField: React.FC<JsonInputFieldProps> = ({
                                                           index,
                                                           setApis,
                                                           apis,
                                                           api,
                                                       }) => {
    const { toast } = useToast();

    // Derived initial code from the prop "api.input.body"
    const getInitialCode = () => {
        if (typeof api.input.body === "string") {
            return api.input.body;
        }
        if (Object.keys(api.input.body || {}).length === 0) {
            return "";
        }
        return JSON.stringify(api.input.body, null, 2);
    };

    const [code, setCode] = useState(getInitialCode());

    // Update local state whenever api.input.body changes.
    useEffect(() => {
        setCode(getInitialCode());
    }, [api.input.body]);

    const handleCodeChange = (newCode: string) => {
        setCode(newCode);
    };

    const validateCodeChange = () => {
        try {
            // If valid JSON, format it
            const parsed = JSON.parse(code);
            const formatted = JSON.stringify(parsed, null, 2);
            setApis(
                apis.map((apiObj, i) =>
                    i === index
                        ? {
                            ...apiObj,
                            input: { ...apiObj.input, body: parsed },
                        }
                        : apiObj
                )
            );
            setCode(formatted);
        } catch (error) {
            // Save raw value if not valid JSON
            setApis(
                apis.map((apiObj, i) =>
                    i === index
                        ? {
                            ...apiObj,
                            input: { ...apiObj.input, body: code },
                        }
                        : apiObj
                )
            );
            toast({
                description: "Not valid JSON – saved as plain text.",
                className: "bg-yellow-500 text-white",
            });
        }
    };

    return (
        <Accordion
            type="single"
            collapsible
            className="w-[90%]"
            defaultValue={"input-json-" + index}
        >
            <AccordionItem value={"input-json-" + index} className="mt-4">
                <AccordionTrigger className="text-white text-sm inputoutput font-bold tracking-wide">
                    JSON
                </AccordionTrigger>
                <AccordionContent>
                    <AceEditor
                        style={{ height: "auto", minHeight: "100px" }}
                        mode="json"
                        theme="tomorrow"
                        value={code}
                        onChange={handleCodeChange}
                        onBlur={validateCodeChange}
                        name={"UNIQUE_INPUT_DIV_" + index}
                        editorProps={{ $blockScrolling: true }}
                        setOptions={{
                            showLineNumbers: true,
                            tabSize: 2,
                        }}
                    />
                </AccordionContent>
            </AccordionItem>
            <AccordionItem value={"input-description-" + index}>
                <AccordionTrigger className="text-white text-sm inputoutput font-bold tracking-wide">
                    Description
                </AccordionTrigger>
                <AccordionContent>
                    <Textarea
                        value={api.input.description}
                        onChange={(e) =>
                            setApis(
                                apis.map((apiObj, i) =>
                                    i === index
                                        ? {
                                            ...apiObj,
                                            input: {
                                                ...apiObj.input,
                                                description: e.target.value,
                                            },
                                        }
                                        : apiObj
                                )
                            )
                        }
                    />
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
};

export default JsonInputField;
